import { useMutation } from '@apollo/client';

import {
  CreateInternationalTransferRecipientInput,
  InternationalTransferProviderErrorResponse,
  InternationalTransferRecipient,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useBankTransferContext } from 'features/Payments/Pages/Transfers/contexts';

import { InternationalTransferRecipientsDocument } from '../../../InternationalTransferRecipientList/graphql/internationalTransferRecipients.gql';
import locales from '../../locales';
import {
  CreateInternationalTransferRecipientDocument,
  CreateInternationalTransferRecipientMutation,
} from './graphql/internationalTransferRecipientCreate.gql';

const isResponseSuccess = (
  obj: CreateInternationalTransferRecipientMutation['createInternationalTransferRecipient'],
): obj is InternationalTransferRecipient => {
  if (!obj) {
    return false;
  }

  return 'internationalTransferRecipientId' in obj;
};

const isResponseError = (
  obj: CreateInternationalTransferRecipientMutation['createInternationalTransferRecipient'],
): obj is InternationalTransferProviderErrorResponse => {
  if (!obj) {
    return false;
  }

  return !('internationalTransferRecipientId' in obj);
};

const getRecipientAndErrorsFromData = (
  data: CreateInternationalTransferRecipientMutation | null | undefined,
) => {
  if (!data) {
    return {
      errors: null,
      recipient: null,
    };
  }

  const recipient = isResponseSuccess(data.createInternationalTransferRecipient)
    ? data.createInternationalTransferRecipient
    : null;

  const errors = isResponseError(data.createInternationalTransferRecipient)
    ? data.createInternationalTransferRecipient.errors
    : null;

  return {
    errors,
    recipient,
  };
};

export const useCreateInternationalTransferRecipient = () => {
  const { companyProfileId, companyUserId } = useCompanyContext();

  const {
    bankTransfer: {
      currency,
      quote: { internationalTransferQuoteId },
    },
  } = useBankTransferContext('internationalTransferRecipient');

  const [mutate, { data, loading: isCreatingRecipient }] = useMutation(
    CreateInternationalTransferRecipientDocument,
    {
      refetchQueries: [
        {
          query: InternationalTransferRecipientsDocument,
          variables: {
            input: {
              companyProfileId,
              companyUserId,
              currency: currency.code,
              internationalTransferQuoteId,
              lang: locales.getLanguage(),
            },
          },
        },
      ],
    },
  );

  const createInternationalTransferRecipient = async (
    input: CreateInternationalTransferRecipientInput,
    shortLivedToken: string,
  ) => {
    const { data: mutateData } = await mutate({
      context: {
        headers: {
          'short-lived-token': shortLivedToken,
        },
      },
      variables: {
        input,
      },
    });

    return {
      isCreatingRecipient,
      ...getRecipientAndErrorsFromData(mutateData),
    };
  };

  return {
    createInternationalTransferRecipient,
    isCreatingRecipient,
    ...getRecipientAndErrorsFromData(data),
  };
};
